import type { FC } from 'react'
import {
    Alert,
    Anchor,
    Badge,
    Box,
    Card,
    Group,
    Skeleton,
    Stack,
    Text,
    useComputedColorScheme,
} from '@mantine/core'
import {
    IconAlertTriangle,
    IconShoppingCart,
    IconTrendingDown,
    IconTrendingUp,
} from '@tabler/icons-react'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title as ChartTitle,
    Tooltip,
    Legend,
} from 'chart.js'
import type { ChartOptions, Plugin, TooltipItem } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { formatDistanceToNow, parseISO } from 'date-fns'
import type { AudienceConfiguration } from '@/lib/domain'
import { formatCompactNumber } from '@/lib/formatNumber'
import {
    chartPalette,
    CHART_GRID_STYLE,
    type ChartPalette,
} from '@/lib/chartColors'
import { nextRefreshAt } from '../api'
import { useAudienceInsights } from '../queries'
import type { AudienceInsightChart } from '../types'

// Registration is global + idempotent; register here so this component stands
// on its own wherever it renders.
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ChartTitle,
    Tooltip,
    Legend
)

// Draws the backend's `display_value` (the lift, e.g. "2.3x avg") directly on top
// of each bar, centered along its length, so the figure is readable without
// hovering. The strings are stashed on the dataset by `dataFor` and read back here
// at draw time. Bars too narrow to hold the label legibly are skipped — the value
// stays available in the tooltip.
const BAR_LABEL_PADDING = 8 // px of bar that must exceed the text width to draw

const barLabelPlugin: Plugin<'bar'> = {
    id: 'audienceInsightBarLabel',
    afterDatasetsDraw(chart) {
        const { ctx } = chart
        const meta = chart.getDatasetMeta(0)
        const labels = (
            chart.data.datasets[0] as {
                displayValues?: (string | null)[]
            }
        ).displayValues
        if (!labels) return

        ctx.save()
        ctx.font = '600 11px sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = '#FFFFFF'
        meta.data.forEach((bar, i) => {
            const text = labels[i]
            if (!text) return
            // Horizontal bars: `base` is the zero edge, `x` the value edge.
            const { x, base, y } = bar.getProps(['x', 'base', 'y'], true) as {
                x: number
                base: number
                y: number
            }
            const barWidth = Math.abs(x - base)
            const textWidth = ctx.measureText(text).width
            if (barWidth < textWidth + BAR_LABEL_PADDING) return
            ctx.fillText(text, (x + base) / 2, y)
        })
        ctx.restore()
    },
}

// The backend encodes the benchmark direction in each chart's title suffix,
// e.g. "TRX: …Frequency: above average". Pull it out so the chart can be marked
// green (above) / red (below) — otherwise above- and below-average charts look
// identical and are easy to misread.
type BenchmarkDirection = 'above' | 'below'

const directionOf = (title: string): BenchmarkDirection | null => {
    const t = title.toLowerCase().trimEnd()
    if (t.endsWith('above average')) return 'above'
    if (t.endsWith('below average')) return 'below'
    return null
}

// Title without the "…: above/below average" suffix — the badge carries it.
const titleWithoutDirection = (title: string): string =>
    title.replace(/:\s*(?:above|below)\s+average\s*$/i, '').trim()

// Scheme-aware bar fill/border per direction, mirroring the app's semantic
// success/error scales (brightened in dark, like chartPalette's sage) so the
// chart reads green/red at a glance. White data labels stay legible on each fill.
const directionBarColor = (
    direction: BenchmarkDirection,
    scheme: 'light' | 'dark'
): { fill: string; border: string } => {
    const palettes = {
        light: {
            above: {
                fill: 'rgba(66, 158, 74, 0.85)', // success 5
                border: 'rgb(44, 133, 54)', // success 6
            },
            below: {
                fill: 'rgba(210, 91, 108, 0.85)', // error 5
                border: 'rgb(179, 70, 86)', // error 6
            },
        },
        dark: {
            above: {
                fill: 'rgba(96, 185, 101, 0.9)', // success 4
                border: '#83CF85', // success 3
            },
            below: {
                fill: 'rgba(238, 119, 133, 0.9)', // error 4
                border: '#FF96A0', // error 3
            },
        },
    }
    return palettes[scheme][direction]
}

// Strip a leading dataset code like "TRX:" so the attribute reads naturally in a
// sentence ("coins and collectibles frequency", not "TRX: Coins…"). Titles with
// no such prefix (e.g. "Brands") pass through unchanged.
const attributeName = (title: string): string =>
    titleWithoutDirection(title)
        .replace(/^[A-Z]{2,5}:\s*/, '')
        .trim()

// Affinity multiplier formatted for the tooltip sentence: 1.5 → "1.5×" (null when the
// chart carries no lift). The backend owns the number; we only format it.
const liftMultiplier = (lift?: number | null): string | null =>
    lift != null ? `${lift.toFixed(1)}×` : null

// ---- Lift-scaled bars ---------------------------------------------------------
// The backend plots distinctiveness for us: each bar's `lift` is the affinity
// multiplier (how many times more/less likely this audience is to fall in the slice vs
// the overall population), and axis-distorting spikes are already trimmed server-side.
// So the chart plots `lift` as the bar length and moves the raw count to the tooltip.
// The chart only decides whether lift is present — live charts carry it; the legacy
// refresh path does not and falls back to the raw count.
const chartHasLift = (chart: AudienceInsightChart): boolean =>
    chart.bars.length > 0 && chart.bars.every((b) => b.lift != null)

// Plain-language sentence for a single bar — what hovering it should explain to a
// non-analyst marketer (decodes the "2.3× avg" lift). Single-attribute charts
// describe the bucket ("have high coins and collectibles frequency"); multi-item
// charts (e.g. Brands) describe the item itself ("buy Kneaders Bakery & Cafe").
const explainBar = (chart: AudienceInsightChart, index: number): string => {
    const bar = chart.bars[index]
    const direction = directionOf(chart.title)
    const mult = liftMultiplier(bar?.lift)
    const likelihood = mult
        ? `${mult} as likely as the average consumer`
        : `${direction === 'below' ? 'less' : 'more'} likely than the average consumer`
    const action =
        chart.bars.length === 1
            ? `have ${bar?.label} ${attributeName(chart.title).toLowerCase()}`
            : `buy ${bar?.label}`
    return `People who match are ${likelihood} to ${action}.`
}

// Word-wrap a sentence into short lines so the Chart.js tooltip (which doesn't
// wrap) stays narrow and readable. Each returned string is rendered as its own
// tooltip line.
const wrapText = (text: string, maxChars = 42): string[] => {
    const lines: string[] = []
    let line = ''
    for (const word of text.split(' ')) {
        if (line && `${line} ${word}`.length > maxChars) {
            lines.push(line)
            line = word
        } else {
            line = line ? `${line} ${word}` : word
        }
    }
    if (line) lines.push(line)
    return lines
}

// Per-chart options: hide the legend, format the value axis compactly, and prefer
// the backend's `display_value` in the tooltip when present.
const optionsFor = (
    chart: AudienceInsightChart,
    pal: ChartPalette,
    useLift: boolean
): ChartOptions<'bar'> => ({
    // Horizontal bars: categories run down the y-axis, values along the x-axis
    // (so the compact-number formatter lives on x). Reads better for long labels.
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                // A full plain-language sentence per bar (wrapped to stay narrow),
                // so hovering explains the lift without analyst jargon. The bucket
                // label is already on the y-axis, so the default title is dropped.
                title: () => '',
                // The lift sentence, plus the raw count (the marketer's "how big"
                // follow-up) since the bar no longer encodes it.
                label: (ctx: TooltipItem<'bar'>) => {
                    const lines = wrapText(explainBar(chart, ctx.dataIndex))
                    const count = chart.bars[ctx.dataIndex]?.value
                    if (count != null) {
                        lines.push(
                            `≈ ${formatCompactNumber(count)} matching people`
                        )
                    }
                    return lines
                },
            },
        },
    },
    scales: {
        x: {
            // Lift is a small multiplier anchored at 0; the legacy count fallback
            // uses the compact-number formatter.
            ...(useLift ? { min: 0 } : {}),
            ticks: {
                color: pal.tick,
                callback: (value) =>
                    useLift
                        ? `${Number(value)}x`
                        : formatCompactNumber(Number(value)),
            },
            grid: { color: pal.grid },
        },
        y: {
            ticks: { color: pal.tick },
            grid: { color: pal.grid },
        },
    },
})

const dataFor = (
    chart: AudienceInsightChart,
    bar: { fill: string; border: string },
    useLift: boolean
) => ({
    labels: chart.bars.map((b) => b.label),
    datasets: [
        {
            label: chart.title,
            // Bar length is the affinity multiplier (lift); the legacy path with no
            // lift falls back to the raw count.
            data: useLift
                ? chart.bars.map((b) => b.lift ?? 0)
                : chart.bars.map((b) => b.value),
            backgroundColor: bar.fill,
            borderColor: bar.border,
            borderWidth: 1,
            // Stashed for `barLabelPlugin`, which reads it off the dataset at draw time.
            displayValues: chart.bars.map((b) => b.display_value ?? null),
        },
    ],
})

export interface AudienceInsightsProps {
    config: AudienceConfiguration
}

// Real "what this audience buys" charts for any segment — transaction,
// user-attribute, or crossed. Each type has its own LLM endpoint (selected in the
// hook) but shares this component, the cache, and the refresh. This is what the
// Makeup tab renders (it replaced an earlier mock-data summary).
export const AudienceInsights: FC<AudienceInsightsProps> = ({ config }) => {
    const { data, isLoading, isError, error, isFetching, regenerate } =
        useAudienceInsights(config)
    // Scheme-aware chart colors (canvas can't read CSS vars, so resolve here);
    // re-renders on the light/dark toggle so the bars + axes recolor.
    const scheme = useComputedColorScheme('light')
    const pal = chartPalette(scheme)

    return (
        <Stack gap='md'>
            <Text size='xs' c='dimmed' tt='uppercase' fw={600} mb='sm'>
                <IconShoppingCart
                    size={14}
                    style={{ verticalAlign: 'text-bottom' }}
                />{' '}
                What this audience buys
            </Text>

            {/* Skeletons during any in-flight fetch — the initial load and a
                regenerate alike — so it's clear the charts are being (re)built. */}
            {isLoading || isFetching ? (
                <Box style={CHART_GRID_STYLE}>
                    <Skeleton height={260} radius='md' />
                    <Skeleton height={260} radius='md' />
                </Box>
            ) : isError ? (
                <Alert
                    color='error'
                    icon={<IconAlertTriangle size={18} />}
                    title="Couldn't load audience insights"
                >
                    {error instanceof Error
                        ? error.message
                        : 'Something went wrong loading these charts.'}
                </Alert>
            ) : !data || data.charts.length === 0 ? (
                <Stack gap='xs' align='flex-start'>
                    <Text size='sm' c='dimmed'>
                        No purchase insights for this segment yet.
                    </Text>
                    <Anchor
                        component='button'
                        type='button'
                        size='sm'
                        onClick={() => void regenerate()}
                    >
                        Retry
                    </Anchor>
                </Stack>
            ) : (
                <>
                    {data.fetchedAt && (
                        <Text size='xs' c='dimmed' mt={-8}>
                            Data run{' '}
                            {formatDistanceToNow(parseISO(data.fetchedAt), {
                                addSuffix: true,
                            })}{' '}
                            · next refresh{' '}
                            {formatDistanceToNow(nextRefreshAt(Date.now()), {
                                addSuffix: true,
                            })}
                        </Text>
                    )}
                    <Box style={CHART_GRID_STYLE}>
                        {data.charts.map((chart) => {
                            const direction = directionOf(chart.title)
                            const bar = direction
                                ? directionBarColor(direction, scheme)
                                : pal.bar
                            // Plot lift (distinctiveness) when the backend provides it;
                            // legacy lift-less charts keep their raw-count rendering.
                            const useLift = chartHasLift(chart)
                            return (
                                <Card
                                    key={chart.title}
                                    withBorder
                                    radius='md'
                                    padding='md'
                                >
                                    {/* Reserve two lines of title height on every
                                        card so a one-line title and a wrapped
                                        two-line title start their charts at the
                                        same baseline (no floating misalignment).
                                        sm line-height is 1.45 (theme). */}
                                    <Group
                                        justify='space-between'
                                        wrap='nowrap'
                                        align='flex-start'
                                        gap='xs'
                                        mb='xs'
                                        mih='calc(2 * 1.45 * var(--mantine-font-size-sm))'
                                    >
                                        <Text size='sm' fw={600}>
                                            {direction
                                                ? titleWithoutDirection(
                                                      chart.title
                                                  )
                                                : chart.title}
                                        </Text>
                                        {direction && (
                                            <Badge
                                                variant='light'
                                                color={
                                                    direction === 'above'
                                                        ? 'success'
                                                        : 'error'
                                                }
                                                leftSection={
                                                    direction === 'above' ? (
                                                        <IconTrendingUp
                                                            size={12}
                                                        />
                                                    ) : (
                                                        <IconTrendingDown
                                                            size={12}
                                                        />
                                                    )
                                                }
                                                style={{ flexShrink: 0 }}
                                            >
                                                {direction === 'above'
                                                    ? 'Above average'
                                                    : 'Below average'}
                                            </Badge>
                                        )}
                                    </Group>
                                    <Box style={{ height: 260 }}>
                                        <Bar
                                            options={optionsFor(
                                                chart,
                                                pal,
                                                useLift
                                            )}
                                            data={dataFor(chart, bar, useLift)}
                                            plugins={[barLabelPlugin]}
                                        />
                                    </Box>
                                </Card>
                            )
                        })}
                    </Box>
                    {data.generatedAt && (
                        <Group justify='flex-end'>
                            <Anchor
                                component='button'
                                type='button'
                                size='xs'
                                c='dimmed'
                                onClick={() => void regenerate()}
                            >
                                Insights created by AI{' '}
                                {formatDistanceToNow(
                                    parseISO(data.generatedAt),
                                    { addSuffix: true }
                                )}
                            </Anchor>
                        </Group>
                    )}
                </>
            )}
        </Stack>
    )
}
